import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, HelpCircle, BarChart3, Trophy, User, Crown, MessageCircle, Wallet } from 'lucide-react';
import { cn } from '../../utils/cn';
import { ThemeToggle } from '../ui/ThemeToggle';
import { StreamerModeToggle } from '../ui/StreamerModeToggle';
import { useAccessControl } from '../../contexts/AccessControlContext';
import { useBalance } from '../../hooks/usePortfolio';

interface MobileMenuProps {
  onHelpClick: () => void;
  onStatsClick: () => void;
  onLeaderboardClick: () => void;
  onUserClick: () => void;
  onPremiumClick: () => void;
  onDiscussionClick: () => void;
  isUserLoggedIn?: boolean;
  className?: string;
  /** When true, hide game-specific items (Statistics, Discussion, Leaderboard, Coins, Compact View) */
  minimal?: boolean;
}

/**
 * MobileMenu component - Hamburger menu for mobile navigation
 * Provides a dropdown menu with all navigation options optimized for touch interaction
 */
export const MobileMenu: React.FC<MobileMenuProps> = ({
  onHelpClick,
  onStatsClick,
  onLeaderboardClick,
  onUserClick,
  onPremiumClick,
  onDiscussionClick,
  isUserLoggedIn = false,
  className,
  minimal = false
}) => {
  const navigate = useNavigate();
  const { shouldShowPremiumButton } = useAccessControl();
  const { data: balance } = useBalance();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Mobile-safe scroll lock - prevents viewport expansion
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Match Modal.tsx cleanup behavior
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleMenuItemClick = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  const menuItems = [
    // Conditionally include premium button based on UI control
    ...(shouldShowPremiumButton ? [{
      icon: Crown,
      label: 'Premium',
      onClick: onPremiumClick,
      highlight: true
    }] : []),
    // Portfolio button - hidden in minimal mode
    ...(!minimal ? [{
      icon: Wallet,
      label: `Coins: $${(balance?.balanceCoins || 0).toLocaleString()}`,
      onClick: () => navigate('/portfolio')
    }] : []),
    {
      icon: User,
      label: isUserLoggedIn ? 'Profile' : 'Login / Sign up',
      onClick: onUserClick
    },
    // Game-specific items - hidden in minimal mode
    ...(!minimal ? [
      {
        icon: BarChart3,
        label: 'Statistics',
        onClick: onStatsClick
      },
      {
        icon: MessageCircle,
        label: 'Discussion',
        onClick: onDiscussionClick
      },
      {
        icon: Trophy,
        label: 'Leaderboard',
        onClick: onLeaderboardClick
      }
    ] : []),
    {
      icon: HelpCircle,
      label: 'Help',
      onClick: onHelpClick
    }
  ];

  return (
    <div ref={menuRef} className={cn('relative', className)}>
      {/* Hamburger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'inline-flex items-center justify-center',
          'w-10 h-10 rounded-lg',
          'transition-all duration-200 ease-in-out',
          'focus:outline-none focus:ring-2 focus:ring-offset-2',
          'hover:scale-105 active:scale-95',
          // Mobile touch target
          'mobile-touch-target',
          // Light theme styles
          'bg-gray-100 hover:bg-gray-200 text-gray-700',
          'focus:ring-gray-300 focus:ring-offset-white',
          // Dark theme styles
          'dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300',
          'dark:focus:ring-gray-600 dark:focus:ring-offset-gray-900'
        )}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {isOpen ? (
          <X className="w-5 h-5" aria-hidden="true" />
        ) : (
          <Menu className="w-5 h-5" aria-hidden="true" />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 z-40"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Menu Panel */}
          <div
            className={cn(
              'fixed right-0 top-[4rem] z-50',
              'w-64 max-h-[calc(100vh-5rem)]',
              'bg-background border border-border rounded-lg shadow-lg',
              'overflow-y-auto',
              'animate-in slide-in-from-right duration-200',
              'mr-4'
            )}
            role="menu"
            aria-label="Mobile navigation menu"
          >
            <div className="p-2 space-y-1">
              {/* Theme Toggle */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <span className="text-sm font-medium text-foreground">Theme</span>
                <ThemeToggle />
              </div>

              {/* Streamer Mode Toggle - hidden in minimal mode */}
              {!minimal && (
                <div className="flex items-center justify-between px-4 py-3 border-b border-border mb-2">
                  <span className="text-sm font-medium text-foreground">Compact View</span>
                  <StreamerModeToggle />
                </div>
              )}

              {/* Menu Items */}
              {menuItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <button
                    key={index}
                    onClick={() => handleMenuItemClick(item.onClick)}
                    className={cn(
                      'w-full flex items-center gap-3',
                      'px-4 py-3 rounded-md',
                      'text-left font-medium',
                      'transition-colors duration-200',
                      'mobile-touch-target',
                      item.highlight
                        ? 'bg-primary/10 text-primary hover:bg-primary/20'
                        : 'text-foreground hover:bg-muted'
                    )}
                    role="menuitem"
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                    <span className="text-sm">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MobileMenu;
